import { Injectable, OnDestroy } from '@angular/core';
import { SwUpdate } from '@angular/service-worker';
import { TranslateService } from '@ngx-translate/core';
import { UtilsService } from '@services/utils.service';
import { getTranslation } from '@shared/translate-helper';

@Injectable({ providedIn: 'root' })
export class ServiceWorkerService implements OnDestroy {
  private updateInterval: any = null; // Store the interval reference
  private isSubscribed = false; // Ensure subscription happens only once

  constructor(
    private swUpdates: SwUpdate,
    private utilsService: UtilsService,
    private translateService: TranslateService,
  ) {
    // Kill interval on unload (e.g., page refresh or close)
    window.addEventListener('beforeunload', this.clearUpdateInterval.bind(this));
  }

  checkForUpdates(): void {
    if (this.swUpdates.isEnabled && !this.isSubscribed) {
      this.isSubscribed = true;

      // Set interval only once
      if (!this.updateInterval) {
        this.updateInterval = setInterval(() => {
          this.swUpdates
            .checkForUpdate()
            .then((hasUpdate) => {
              if (hasUpdate) {
                this.notifyUserToUpdate();
              }
            })
            .catch((err) => {
              console.error('Error when checking for update', err);
              this.forceCleanRefresh();
            });
        }, 300000);
      }

      this.swUpdates.versionUpdates.subscribe((event) => {
        if (event.type === 'VERSION_READY') {
          console.log(
            `A new version is available. Current: ${event.currentVersion}, Available: ${event.latestVersion}`,
          );
          this.notifyUserToUpdate();
        }

        if (event.type === 'VERSION_INSTALLATION_FAILED') {
          console.error('Version installation failed', event);
          // Force a clean refresh to recover from hash mismatch / bad cache
          this.forceCleanRefresh();
        }
      });
    }
  }

  private async forceCleanRefresh(): Promise<void> {
    try {
      // Best-effort: clear caches
      if ('caches' in window) {
        const keys = await caches.keys();
        await Promise.all(keys.map((k) => caches.delete(k)));
      }
      // Best-effort: unregister all service workers
      if ('serviceWorker' in navigator) {
        const regs = await navigator.serviceWorker.getRegistrations();
        await Promise.all(regs.map((r) => r.unregister()));
      }
    } finally {
      // Reload bypassing caches
      const url = new URL(window.location.href);
      url.searchParams.set('v', Date.now().toString());
      window.location.replace(url.toString());
    }
  }

  private notifyUserToUpdate(): void {
    getTranslation(this.translateService, ['update_available', 'reload_to_update', 'reload_now']).subscribe((t) => {
      this.utilsService.showAlert({
        title: t['update_available'],
        alertDescription: t['reload_to_update'],
        icon: 'arrow-rotate-right',
        alertType: 'info',
        showActionBtn: true,
        actionText: t['reload_now'],
        disableClose: true,
        onAction: () => {
          this.swUpdates.activateUpdate().then(() => {
            window.location.reload();
          });
        },
      });
    });
  }

  handleUnrecoverableState(): void {
    this.swUpdates.unrecoverable.subscribe((event) => {
      getTranslation(this.translateService, ['unrecoverable_error', 'please_reload']).subscribe((t) => {
        alert(t['unrecoverable_error'] + ': \n' + event.reason + '\n\n' + t['please_reload']);
      });
      setTimeout(() => {
        window.location.reload();
      }, 3000);
    });
  }

  // Clear interval on unload
  private clearUpdateInterval(): void {
    if (this.updateInterval) {
      clearInterval(this.updateInterval);
      this.updateInterval = null;
    }
  }

  // Clear interval when the service is destroyed
  ngOnDestroy(): void {
    this.clearUpdateInterval();
    window.removeEventListener('beforeunload', this.clearUpdateInterval.bind(this));
  }
}
